<template>
  <div class="caliber-content">
    <div class="caliber-details-container">
      <div class="caliber-details-container-header">
        {{ titleText }}
        <AuditInformation
          v-if="!loading && caliber && caliber.id > 0 && caliber.createdByUser && caliber.createdDate && caliber.lastModifiedByUser && caliber.lastModifiedDate"
          :created-by-user="caliber.createdByUser"
          :created-date="caliber.createdDate"
          :last-modified-by-user="caliber.lastModifiedByUser"
          :last-modified-date="caliber.lastModifiedDate"
        />
      </div>
      <LoadingPulse
        :loading="loading"
        class="loading-spinner"
      />
      <div
        v-if="!loading && caliber"
        class="caliber-details-container-content"
      >
        <LockingButton
          class="save-button"
          :disabled="!isDirty"
          :locked="saving"
          :text="'Save'"
          @click="onSaveCaliberClicked"
        />
        <TextInput
          v-model="caliber.name"
          :show-required="true"
          label="Name"
          class="input"
        />
        <NumericInput
          v-model="caliber.boreDiameter"
          :show-required="true"
          label="Bore Diameter"
          class="input"
        />
      </div>
    </div>
    <div class="cartridges-container">
      <div class="cartridges-container-header">
        <div class="cartridges-container-header-text">
          Cartridges
        </div>
        <a 
          v-show="!loading && caliber && caliber.id > 0"
          class="add-cartridge"
          @click="onAddCartridgeClicked"
        >
          Create
        </a>
      </div>
      <LoadingPulse
        :loading="loading"
        class="loading-spinner"
      />
      <div
        v-if="!loading"
        class="overflow-wrapper"
      >
        <table>
          <thead>
            <tr>
              <th class="no-sort">
                <div>
                  Name
                </div>
              </th>
              <th class="no-sort">
                <div>
                  Diameter Minimum
                </div>
              </th>
              <th class="no-sort" />
            </tr>
          </thead>
          <tbody v-if="caliber && caliber.cartridges.length > 0">
            <tr
              v-for="cartridge in caliber.cartridges"
              :key="cartridge.id"
              class="clickable"
              @click="onTableRowClicked(cartridge)"
            >
              <td>{{ cartridge.name }}</td>
              <td>{{ cartridge.diameterMinimum }}</td>
              <td class="delete">
                <TrashCanSvg @click.stop="onDeleteCartridgeClicked(cartridge.id)" />
              </td>
            </tr>
          </tbody>
          <tbody v-else>
            <tr>
              <td
                v-if="caliber && caliber.id > 0"
                :colspan="3"
                class="message"
              >
                There are no cartridges on the caliber. Please click 'Create' above to add a cartridge.
              </td>
              <td
                v-else
                :colspan="3"
                class="message"
              >
                Cartridges can be added to the caliber once it is initally created.
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
    <transition name="slide-up">
      <CartridgeModal
        v-if="showCartridgeModal && cartridgeToEdit"
        :caliber-id="id"
        :cartridge="cartridgeToEdit"
        @cartridge-created="onCartridgeCreated"
        @cartridge-updated="onCartridgeUpdated"
        @close="showCartridgeModal = false"
      />
    </transition>
  </div>
</template>

<script lang="ts">
import { defineComponent, inject } from 'vue'
import NumericInput from '@/components/shared/NumericInput.vue'
import TextInput from '@/components/shared/TextInput.vue'
import LoadingPulse from '@/components/shared/LoadingPulse.vue'
import { PROVIDER_CONSTANTS } from '@/constants/provider.constants'
import { CaliberService } from '@/services/caliber.service'
import Toaster from '@meforma/vue-toaster'
import CartridgeModal from '@/components/modals/CartridgeModal.vue'
import { Cartridge } from '@/@types/responses/cartridge/Cartridge'
import { CaliberDetails } from '@/@types/responses/caliber/CaliberDetails'
import TrashCanSvg from '@/components/svgs/TrashCanSvg.vue'
import { CartridgeService } from '@/services/cartridge.service'
import AuditInformation from '@/components/elements/AuditInformation.vue'
import { CaliberMessage } from '@/@types/messages/CaliberMessage'
import LockingButton from '@/components/shared/LockingButton.vue'
import { ErrorsResponse } from '@/@types/responses/common/ErrorsResponse'

export default defineComponent({
  name: 'Caliber',
  components: {
    AuditInformation,
    CartridgeModal,
    LoadingPulse,
    LockingButton,
    NumericInput,
    TextInput,
    TrashCanSvg
  },
  beforeRouteLeave() {
    if (this.showCartridgeModal) {
      this.showCartridgeModal = false
      return false
    }

    return true
  },
  props: {
    id: {
      type: Number,
      required: true
    }
  },
  setup() {
    const caliberService = inject(PROVIDER_CONSTANTS.caliberService) as CaliberService
    const cartridgeService = inject(PROVIDER_CONSTANTS.cartridgeService) as CartridgeService
    const toast = inject(PROVIDER_CONSTANTS.toaster) as Toaster

    return {
      caliberService,
      cartridgeService,
      toast
    }
  },
  data() {
    let caliber: CaliberDetails|undefined
    let unchangedCaliber: CaliberDetails|undefined
    let cartridgeToEdit: Cartridge|undefined

    return {
      caliber,
      unchangedCaliber,
      cartridgeToEdit,
      loading: true,
      saving: false,
      showCartridgeModal: false
    }
  },
  computed: {
    isDirty(): boolean {
      const a = this.caliber
      const b = this.unchangedCaliber

      return (a?.name !== b?.name ||
              a?.boreDiameter !== b?.boreDiameter) && (a?.name.trim() !== '' && a?.boreDiameter !== undefined && a?.boreDiameter >= 0)
    },
    titleText(): string {
      return !this.caliber || this.caliber.id === 0 ? 'Create Caliber' : 'Edit Caliber'
    }
  },
  watch: {
    '$route': {
      handler: function(): void {
        this.updatePageTitle()
      }
    }
  },
  mounted() {
    if (this.id > 0) {
      this.getCaliber()
    } else {
      this.caliber = new CaliberDetails(0)
      this.unchangedCaliber = new CaliberDetails(0)
      this.loading = false
    }
  },
  methods: {
    getCaliber(): Promise<void> {
      return this.caliberService.getCaliber(this.caliber ? this.caliber.id : this.id).then(caliber => {
        this.caliber = caliber
        this.unchangedCaliber = JSON.parse(JSON.stringify(caliber))
        this.updatePageTitle()
        this.loading = false
      }).catch(error => {
        this.saving = false

        if (error instanceof ErrorsResponse) {
          const errorsResponse = error as ErrorsResponse

          if (errorsResponse.errors.some(e => e.id === 33001 || e.id === 33002 || e.id === 33003)) return

          this.toast.error(errorsResponse.errors.map(e => e.message).join("\r\n"))
        } else {
          this.toast.error('Failed to load the caliber. Try reloading the page.')
        }
      })
    },
    onAddCartridgeClicked(): void {
      this.cartridgeToEdit = new Cartridge(0)
      this.showCartridgeModal = true
    },
    onCartridgeCreated(): void {
      this.getCaliber().then(() => {
        this.toast.success('Cartridge created successfully.')
      })
    },
    onCartridgeUpdated(): void {
      this.getCaliber().then(() => {
        this.toast.success('Cartridge updated successfully.')
      })
    },
    onDeleteCartridgeClicked(cartridgeId: number): void {
      this.cartridgeService.deleteCartridge(cartridgeId).then(() => {
        this.getCaliber().then(() => {
          this.toast.success('Cartridge deleted successfully.')
        })
      }).catch(error => {
        if (error instanceof ErrorsResponse) {
          const errorsResponse = error as ErrorsResponse

          if (errorsResponse.errors.some(e => e.id === 33001 || e.id === 33002 || e.id === 33003)) return

          this.toast.error(errorsResponse.errors.map(e => e.message).join("\r\n"))
        } else {
          this.toast.error('Failed to delete the cartridge. Please try again.')
        }
      })
    },
    onSaveCaliberClicked(): void {
      if (!this.caliber) return

      this.saving = true

      const caliberMessage = new CaliberMessage(this.caliber.name, this.caliber.boreDiameter)
      if (this.caliber.id === 0)
        this.caliberService.createCaliber(caliberMessage).then(objectCreatedResponse => {
          if (!this.caliber) return

          this.caliber.id = objectCreatedResponse.id
          this.getCaliber().then(() => {
            this.$router.replace(`/caliber/${objectCreatedResponse.id}`)
            this.saving = false
            this.toast.success('The caliber was created successfully.')
          })
        }).catch(error => {
          this.saving = false

          if (error instanceof ErrorsResponse) {
            const errorsResponse = error as ErrorsResponse

            if (errorsResponse.errors.some(e => e.id === 33001 || e.id === 33002 || e.id === 33003)) return

            this.toast.error(errorsResponse.errors.map(e => e.message).join("\r\n"))
          } else {
            this.toast.error('Failed to create the caliber. Please try again.')
          }
        })
      else
        this.caliberService.updateCaliber(this.id, caliberMessage).then(() => {
          this.toast.success('The caliber was created successfully.')
          this.$router.push('/calibers')
        }).catch(error => {
          this.saving = false

          if (error instanceof ErrorsResponse) {
            const errorsResponse = error as ErrorsResponse

            if (errorsResponse.errors.some(e => e.id === 33001 || e.id === 33002 || e.id === 33003)) return

            this.toast.error(errorsResponse.errors.map(e => e.message).join("\r\n"))
          } else {
            this.toast.error('Failed to update the caliber. Please try again.')
          }
        })
    },
    onTableRowClicked(cartridge: Cartridge): void {
      this.cartridgeToEdit = cartridge
      this.showCartridgeModal = true
    },
    updatePageTitle(): void {
      if (this.id > 0 && this.caliber)
        document.title = `${this.caliber.name} - Short Action Customs, LLC`
    }
  }
})
</script>

<style scoped lang="scss">
.caliber-content {
  display: flex;
  flex-direction: column;
  padding: 15px;

  @include desktop {
    flex-direction: row;
  }

  > .caliber-details-container {
    display: flex;
    flex-direction: column;
    margin-bottom: 15px;
    position: relative;

    @include desktop {
      flex: 1 1 25%;
      height: max-content;
      padding: 15px;
      background-color: $Color-fill-gray;
      border-radius: 2px;
      @include element-shadow;
    }

    > .caliber-details-container-header {
      line-height: 37px;
      font-size: 24px;
      font-weight: bold;
      padding-bottom: 10px;
      border-bottom: 1px solid $Color-border-gray;
    }

    > .loading-spinner {
      margin: auto;
      padding: 20px;
    }

    > .caliber-details-container-content {
      padding-top: 15px;

      > .save-button {
        position: absolute;
        top: 0;
        right: 0;

        @include desktop {
          top: 15px;
          right: 15px;
        }
      }

      > .input {
        margin-bottom: 15px;

        &:last-of-type {
          margin-bottom: 0;
        }
      }
    }
  }

  > .cartridges-container {
    position: relative;
    height: 100%;
    display: flex;
    flex-direction: column;
    padding-bottom: 15px;

    @include desktop {
      flex: 1 1 75%;
      width: 75%;
      padding-left: 20px;
    }

    > .cartridges-container-header {
      display: flex;
      margin-bottom: 15px;

      > .cartridges-container-header-text {
        margin: 0 auto 0 0;
        font-size: 24px;
        font-weight: bold;
      }

      > .add-cartridge {
        @include flat-button;
        margin: auto 0;
      }
    }

    > .loading-spinner {
      margin: auto;
    }

    > .overflow-wrapper {
      overflow-y: auto;
      @include sac-table;
    }
  }

  > .slide-up {
    @include modal-animation;
  }
}
</style>
